import {
  CheckCircle as CheckCircleIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Error as ErrorIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  downloadIftaTemplate,
  enqueueIftaReport,
  getIftaEligibility,
  IFTA_JOB_SESSION_KEY,
  IftaEligibility,
  IftaGenerateRequest,
  IftaPaymentRequiredError,
  IftaPlanNotAllowedError,
  IftaValidationResult,
  useGetIftaProfileQuery,
  useValidateIftaUploadMutation,
} from "../../api/reportsApi";
import IftaProfileDialog from "./IftaProfileDialog";
import { useAppSelector } from "../../app/hooks";
import { Column, DataTable } from "../../components/common/DataTable/DataTable";
import PageTitle from "../../components/common/PageTitle";
import { ReportsIcon } from "../../components/common/pageIcons";
import PageLayout from "../../components/layout/PageLayout";
import TabbedPageCard from "../../components/common/TabbedPageCard";
import { useSignalR } from "../../contexts/SignalRContext";
import IftaGenerationsList from "./IftaGenerationsList";
import IftaPaymentDialog from "./IftaPaymentDialog";
import IftaTaxAdminPanel from "./IftaTaxAdminPanel";
import QuarterPicker, { currentQuarter } from "./QuarterPicker";

const formatAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
};

type PendingIftaJob = {
  jobId: string;
  quarter: string;
  wasPaid?: boolean;
  truckCount?: number;
};

const ALLOWED_ROLES = ["Admin", "Fleet", "Accounting", "Safety", "SuperAdmin", "IftaAdmin"];
const TENANT_REPORT_ROLES = ["Admin", "Fleet", "Accounting", "Safety", "SuperAdmin", "IftaAdmin"];

const SESSION_KEY = IFTA_JOB_SESSION_KEY;

const IftaReportPage: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token) ?? "";
  const userRole = useAppSelector((state) => state.auth.user?.role ?? "");
  const { connection } = useSignalR();

  // ── Form state ─────────────────────────────────────────────────────────────
  const [quarter, setQuarter] = useState<string>(currentQuarter());
  const [hasTrucksNotHandled, setHasTrucksNotHandled] = useState(false);
  const [hasMilesWithoutIvg, setHasMilesWithoutIvg] = useState(false);
  const [hasFuelWithoutCard, setHasFuelWithoutCard] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // ── Request state ──────────────────────────────────────────────────────────
  const [validationResult, setValidationResult] =
    useState<IftaValidationResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [paidGenerationFailed, setPaidGenerationFailed] = useState(false);

  // ── Billing / eligibility state ────────────────────────────────────────────
  const [eligibility, setEligibility] = useState<IftaEligibility | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useGetIftaProfileQuery();
  const continueAfterProfileRef = useRef<(() => void) | null>(null);

  // ── Background job state ───────────────────────────────────────────────────
  // Persisted in sessionStorage so loading state survives page navigation.
  const pendingJobRef = useRef<PendingIftaJob | null>(null);

  const [validateUpload, { isLoading: isValidating }] =
    useValidateIftaUploadMutation();

  // ── Request builder (file + quarter + flags) ───────────────────────────────
  const buildRequest = useCallback(
    (paymentIntentId?: string): IftaGenerateRequest => ({
      file: uploadedFile,
      quarter,
      hasTrucksNotHandled,
      hasMilesWithoutIvg,
      hasFuelWithoutCard,
      paymentIntentId,
    }),
    [
      uploadedFile,
      quarter,
      hasTrucksNotHandled,
      hasMilesWithoutIvg,
      hasFuelWithoutCard,
    ],
  );

  // ── Load billing eligibility (price depends on trucks in the uploaded file) ─
  const refreshEligibility = useCallback(async () => {
    if (!token || !uploadedFile) {
      setEligibility(null);
      return;
    }
    try {
      const result = await getIftaEligibility(buildRequest(), token);
      setEligibility(result);
    } catch {
      // Non-blocking: if eligibility fails, generation falls back to 402/403 handling.
    }
  }, [token, uploadedFile, buildRequest]);

  useEffect(() => {
    refreshEligibility();
  }, [refreshEligibility]);

  // ── Restore pending job from sessionStorage on mount ──────────────────────
  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const job = JSON.parse(stored) as PendingIftaJob;
        pendingJobRef.current = job;
        setGenerateLoading(true);
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
  }, []);

  // ── Clear pending job helper ───────────────────────────────────────────────
  // NOTE: does NOT touch sessionStorage — AppLayout owns that cleanup to avoid
  // a race condition where both handlers check the same storage key.
  const clearPendingJob = useCallback(() => {
    pendingJobRef.current = null;
    setGenerateLoading(false);
  }, []);

  // ── SignalR: listen for IftaReportReady / IftaReportFailed ────────────────
  useEffect(() => {
    if (!connection) return;

    // Download is handled globally in AppLayout (always mounted).
    // Here we only clean up the page's loading UI state.
    // Use pendingJobRef (not sessionStorage) to avoid race with AppLayout handler.
    const onReady = (payload: { jobId: string }) => {
      if (pendingJobRef.current?.jobId !== payload.jobId) return;
      clearPendingJob();
    };

    const onFailed = (payload: { jobId: string; error: string }) => {
      if (pendingJobRef.current?.jobId !== payload.jobId) return;
      const wasPaid = pendingJobRef.current?.wasPaid === true;
      clearPendingJob();
      setPaidGenerationFailed(wasPaid);
      setGenerateError(
        payload.error || "Report generation failed. Please try again.",
      );
    };

    connection.on("IftaReportReady", onReady);
    connection.on("IftaReportFailed", onFailed);

    return () => {
      connection.off("IftaReportReady", onReady);
      connection.off("IftaReportFailed", onFailed);
    };
  }, [connection, token, clearPendingJob]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const anyCheckboxChecked =
    hasTrucksNotHandled || hasMilesWithoutIvg || hasFuelWithoutCard;
  const canGenerate = validationResult?.valid === true || !anyCheckboxChecked;

  // ── Dropzone ───────────────────────────────────────────────────────────────
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      setValidationResult(null);
      setValidationError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1,
  });

  const handleCheckboxChange =
    (setter: React.Dispatch<React.SetStateAction<boolean>>) =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setter(checked);
      setValidationResult(null);
      setValidationError(null);
    };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    setTemplateError(null);
    try {
      await downloadIftaTemplate(token);
    } catch (err) {
      setTemplateError(
        err instanceof Error ? err.message : "Failed to download template.",
      );
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!uploadedFile) return;
    setValidationResult(null);
    setValidationError(null);
    try {
      const result = await validateUpload({
        file: uploadedFile,
        quarter,
        hasTrucksNotHandled,
        hasMilesWithoutIvg,
        hasFuelWithoutCard,
      }).unwrap();
      setValidationResult(result);
    } catch (err: any) {
      if (err?.status === 422 && err?.data?.data) {
        setValidationResult(err.data.data as IftaValidationResult);
      } else {
        setValidationError(
          err?.data?.message || err?.message || "Validation request failed.",
        );
      }
    }
  };

  const doEnqueue = useCallback(
    async (paymentIntentId?: string) => {
      if (!uploadedFile) {
        setGenerateError(
          "Please upload the data import file before generating.",
        );
        return;
      }

      clearPendingJob();
      setGenerateLoading(true);
      setGenerateError(null);
      setPaidGenerationFailed(false);

      try {
        const result = await enqueueIftaReport(
          buildRequest(paymentIntentId),
          token,
        );
        const job: PendingIftaJob = {
          jobId: result.jobId,
          quarter: result.quarter,
          wasPaid: !!paymentIntentId,
          truckCount: eligibility?.truckCount ?? 0,
        };
        pendingJobRef.current = job;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(job));
        // generateLoading stays true — cleared when SignalR notifies (via AppLayout)
        // A generation was just reserved; the next one may now require payment.
        refreshEligibility();
      } catch (err) {
        if (err instanceof IftaPaymentRequiredError) {
          // Eligibility was stale — refresh and prompt for payment instead of erroring.
          refreshEligibility();
          setPaymentDialogOpen(true);
          setGenerateLoading(false);
          return;
        }
        setGenerateError(
          err instanceof IftaPlanNotAllowedError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to start report generation.",
        );
        setGenerateLoading(false);
      }
    },
    [clearPendingJob, uploadedFile, buildRequest, token, refreshEligibility, eligibility],
  );

  const proceedToGenerate = useCallback(() => {
    if (eligibility?.requiresPayment) {
      setPaymentDialogOpen(true);
      return;
    }
    doEnqueue();
  }, [eligibility, doEnqueue]);

  const handleGenerate = () => {
    setGenerateError(null);
    continueAfterProfileRef.current = proceedToGenerate;
    setProfileDialogOpen(true);
  };

  const handleProfileSaved = () => {
    setProfileDialogOpen(false);
    continueAfterProfileRef.current?.();
    continueAfterProfileRef.current = null;
  };

  const handlePaid = (paymentIntentId: string) => {
    setPaymentDialogOpen(false);
    doEnqueue(paymentIntentId);
  };

  const validationSummaryColumns: Column[] = [
    {
      field: "step",
      headerName: "#",
      width: 56,
    },
    {
      field: "name",
      headerName: "Section",
      renderCell: (value) => <Typography variant="body2">{value}</Typography>,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (value) =>
        value === "ok" ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="OK"
            color="success"
            size="small"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<ErrorIcon />}
            label="Error"
            color="error"
            size="small"
            variant="outlined"
          />
        ),
    },
    {
      field: "message",
      headerName: "Message",
      tooltipValue: (_value, row) =>
        validationResult?.errors?.find((e) => e.step === row.step)?.message,
      renderCell: (_value, row) => {
        const error = validationResult?.errors?.find(
          (e) => e.step === row.step,
        );

        return (
          <Typography
            variant="body2"
            color={error ? "error" : "text.secondary"}
          >
            {error?.message ?? "—"}
          </Typography>
        );
      },
    },
  ];

  if (!ALLOWED_ROLES.includes(userRole)) {
    return (
      <PageLayout>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </PageLayout>
    );
  }

  const isIftaAdmin = userRole === "IftaAdmin";
  const canGenerateReports = TENANT_REPORT_ROLES.includes(userRole);

  return (
    <PageLayout>
      {canGenerateReports ? (
        <>
          <PageTitle
            title="IFTA Quarterly Report"
            icon={<ReportsIcon />}
            subtitle={
              <>
                Generate your IFTA fuel tax report. Download the data import
                template, fill it in, upload and validate, then generate the final
                report. Pricing is $5 per truck per report; your first report
                includes free trucks up to your subscription fleet size.
              </>
            }
          />

          <TabbedPageCard
            idPrefix="ifta-report"
            ariaLabel="IFTA report tabs"
            selectedTab={activeTab}
            onTabChange={setActiveTab}
            panelSx={{ p: 2 }}
            tabs={[
              {
                label: "Generate Report",
                content: (
                  <Stack spacing={3}>
        {/* ── Quarter + Checkboxes ── */}
        <Paper elevation={1} sx={{ borderRadius: 2, p: 3 }}>
          <Typography
            variant="subtitle1"
            color="text.primary"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Quarter &amp; Data Sources
          </Typography>

          <Stack spacing={2}>
            {/* ── Quarter picker ── */}
            <QuarterPicker
              value={quarter}
              onChange={(q) => {
                setQuarter(q);
                setValidationResult(null);
                setValidationError(null);
              }}
            />

            <Box>
              {/* Required sheet notice */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <ErrorIcon
                  sx={{
                    fontSize: 16,
                    mt: "2px",
                    color: "warning.main",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">
                  <strong>Required:</strong> The{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    FuelWithCard
                  </Typography>{" "}
                  sheet must always be filled with fuel card (WEX) purchases for
                  accurate results.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <ErrorIcon
                  sx={{
                    fontSize: 16,
                    mt: "2px",
                    color: "warning.main",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">
                  <strong>Required:</strong> The{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    TrucksHandled
                  </Typography>{" "}
                  sheet must always be filled with the list of company trucks.
                  Additionally, check all that apply to your fleet this quarter
                  — the corresponding sheet must have data.
                </Typography>
              </Box>
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasTrucksNotHandled}
                      onChange={handleCheckboxChange(setHasTrucksNotHandled)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      We have Owner-Operators or leased trucks (IFTA not handled
                      by company)
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {" "}
                        (sheet: TrucksNotHandled)
                      </Typography>
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasMilesWithoutIvg}
                      onChange={handleCheckboxChange(setHasMilesWithoutIvg)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      We have miles driven without IVG device – manual entry
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {" "}
                        (sheet: MilesWithoutIVG)
                      </Typography>
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasFuelWithoutCard}
                      onChange={handleCheckboxChange(setHasFuelWithoutCard)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      We have fuel purchased without company card
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {" "}
                        (sheet: FuelWithoutCard)
                      </Typography>
                    </Typography>
                  }
                />
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {/* ── Template + Upload ── */}
        <Paper elevation={1} sx={{ borderRadius: 2, p: 3 }}>
          <Typography
            variant="subtitle1"
            color="text.primary"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Data Import Template
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Download the Excel template, fill in the required sheets, then
                upload it below.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  templateLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                onClick={handleDownloadTemplate}
                disabled={templateLoading}
              >
                {templateLoading
                  ? "Downloading…"
                  : "Download IFTA Template (.xlsx)"}
              </Button>
              {templateError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {templateError}
                </Alert>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Upload the filled template to validate before generating the
                report.
              </Typography>

              <Paper
                {...getRootProps()}
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: isDragActive ? "action.hover" : "background.paper",
                  borderStyle: "dashed",
                  borderColor: isDragActive ? "primary.main" : "divider",
                  cursor: "pointer",
                  transition: "border-color 0.2s, background-color 0.2s",
                  mb: 1.5,
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon
                  sx={{ fontSize: 36, color: "action.active", mb: 0.5 }}
                />
                <Typography variant="body2">
                  {isDragActive
                    ? "Drop the file here…"
                    : "Drag & drop the filled template here, or click to select"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Accepted: .xlsx, .xls
                </Typography>
                {uploadedFile && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: "action.selected",
                    }}
                  >
                    <DescriptionIcon fontSize="small" color="primary" />
                    <Typography variant="body2">{uploadedFile.name}</Typography>
                  </Box>
                )}
              </Paper>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleValidate}
                  disabled={!uploadedFile || isValidating}
                  startIcon={
                    isValidating ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {isValidating ? "Validating…" : "Validate Upload"}
                </Button>
                {!uploadedFile && anyCheckboxChecked && (
                  <Typography variant="caption" color="error">
                    File is required when checkboxes are checked.
                  </Typography>
                )}
              </Box>
            </Box>
          </Stack>
        </Paper>

        {/* ── Validation result ── */}
        {(validationResult || validationError) && (
          <Paper elevation={1} sx={{ borderRadius: 2, p: 3 }}>
            <Typography
              variant="subtitle1"
              color="text.primary"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Validation Result
            </Typography>

            {validationError && (
              <Alert severity="error">{validationError}</Alert>
            )}

            {validationResult && (
              <Stack spacing={2}>
                <Alert severity={validationResult.valid ? "success" : "error"}>
                  {validationResult.valid
                    ? "All checks passed. Scroll down to generate the report."
                    : "Validation failed. Please fix the issues below and re-upload."}
                </Alert>

                {validationResult.summary?.length > 0 && (
                  <DataTable
                    items={validationResult.summary}
                    columns={validationSummaryColumns}
                    totalCount={validationResult.summary.length}
                    fitContentHeight
                  />
                )}
              </Stack>
            )}
          </Paper>
        )}

        {/* ── Generate ── */}
        <Paper elevation={1} sx={{ borderRadius: 2, p: 3 }}>
          <Typography
            variant="subtitle1"
            color="text.primary"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            Generate Report
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {canGenerate
              ? "Ready to generate. The completed IFTA report will be downloaded as a PDF."
              : "Complete validation before generating the report."}
          </Typography>

          {eligibility && (
            <Alert
              severity={
                !eligibility.planAllowed
                  ? "warning"
                  : eligibility.isExempt || !eligibility.requiresPayment
                    ? "success"
                    : "info"
              }
              sx={{ mb: 2 }}
            >
              {!eligibility.planAllowed
                ? "IFTA reports are available only on the Pro ($15/mo) plan. Please upgrade your plan to generate reports."
                : eligibility.isExempt
                  ? "IFTA reports are free for your account."
                  : eligibility.requiresPayment
                    ? eligibility.isFirstReport
                      ? `Your first IFTA report includes ${eligibility.subscribedTruckQuantity} truck(s) at no charge (your subscribed fleet size). This file lists ${eligibility.truckCount} truck(s) — ${eligibility.billableTruckCount} additional truck(s) × ${formatAmount(
                          eligibility.unitAmount,
                          eligibility.currency,
                        )} = ${formatAmount(
                          eligibility.amount,
                          eligibility.currency,
                        )} will be charged before generation.`
                      : `This report lists ${eligibility.truckCount} truck(s) at ${formatAmount(
                          eligibility.unitAmount,
                          eligibility.currency,
                        )} per truck = ${formatAmount(
                          eligibility.amount,
                          eligibility.currency,
                        )} total. Payment is required before generation.`
                    : eligibility.isFirstReport
                      ? `Your first IFTA report includes ${eligibility.subscribedTruckQuantity} truck(s) at no charge (your subscribed fleet size). This file lists ${eligibility.truckCount} truck(s) — no payment needed.`
                      : "This report is included at no extra charge."}
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={
              generateLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            onClick={handleGenerate}
            disabled={
              !canGenerate ||
              generateLoading ||
              (eligibility ? !eligibility.planAllowed : false)
            }
          >
            {generateLoading
              ? "Generating…"
              : eligibility?.requiresPayment
                ? `Pay & Generate – ${quarter}`
                : `Generate IFTA Report – ${quarter}`}
          </Button>

          {generateLoading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Alert severity="info" sx={{ mt: 1.5 }}>
                <strong>
                  Report is being generated in the background — typically
                  10–15 minutes, depending on fleet size.
                </strong>
                <br />
                Mileage is fetched from GPS tracking across all jurisdictions
                and combined with your uploaded fuel data. You can leave this
                page; the PDF downloads automatically when ready.
              </Alert>
            </Box>
          )}

          {generateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>{generateError}</strong>
              {paidGenerationFailed ? (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Your payment is saved as credit for this report — click{" "}
                    <strong>Pay &amp; Generate</strong> or{" "}
                    <strong>Generate</strong> again at no extra charge.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    If generation keeps failing, contact{" "}
                    <a
                      href="mailto:support@adut.us"
                      style={{ color: "inherit" }}
                    >
                      support@adut.us
                    </a>{" "}
                    with quarter <strong>{quarter}</strong> and we will help
                    (including a refund if needed).
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  If the problem persists, contact{" "}
                  <a
                    href="mailto:support@adut.us"
                    style={{ color: "inherit" }}
                  >
                    support@adut.us
                  </a>{" "}
                  with quarter <strong>{quarter}</strong> and a short
                  description of what happened.
                </Typography>
              )}
            </Alert>
          )}
        </Paper>
                  </Stack>
                ),
              },
              {
                label: "Generated Reports",
                content: <IftaGenerationsList token={token} showHeading={false} />,
              },
              ...(isIftaAdmin
                ? [
                    {
                      label: "IFTA Tax Administration",
                      content: <IftaTaxAdminPanel token={token} />,
                    },
                  ]
                : []),
            ]}
          />

          {eligibility && (
            <IftaPaymentDialog
              open={paymentDialogOpen}
              request={buildRequest()}
              amount={eligibility.amount}
              currency={eligibility.currency}
              unitAmount={eligibility.unitAmount}
              truckCount={eligibility.truckCount}
              billableTruckCount={eligibility.billableTruckCount}
              isFirstReport={eligibility.isFirstReport}
              subscribedTruckQuantity={eligibility.subscribedTruckQuantity}
              token={token}
              onClose={() => setPaymentDialogOpen(false)}
              onPaid={handlePaid}
            />
          )}

          <IftaProfileDialog
            open={profileDialogOpen}
            onClose={() => {
              setProfileDialogOpen(false);
              continueAfterProfileRef.current = null;
            }}
            onSaved={handleProfileSaved}
          />
        </>
      ) : isIftaAdmin ? (
        <>
          <PageTitle
            title="IFTA Quarterly Report"
            icon={<ReportsIcon />}
            subtitle="Manage quarterly tax rates and global surcharge values used in IFTA reports."
          />
          <TabbedPageCard
            idPrefix="ifta-report"
            ariaLabel="IFTA report tabs"
            selectedTab={0}
            panelSx={{ p: 2 }}
            tabs={[
              {
                label: "IFTA Tax Administration",
                content: <IftaTaxAdminPanel token={token} />,
              },
            ]}
          />
        </>
      ) : null}
    </PageLayout>
  );
};

export default IftaReportPage;
